
import { useEffect, useState } from "react"
import {
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Grid,
    Paper,
    IconButton,
    CircularProgress,
    MenuItem,
    DialogContent,
    Dialog,
    DialogTitle,
    InputAdornment,
} from "@mui/material"
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    LocationOn as LocationOnIcon,
    Payment as PaymentIcon,
    Security as SecurityIcon,
    AccountBalance as AccountBalanceIcon,
    CreditCard as CreditCardIcon,
    CreditCard,
    Close,
} from "@mui/icons-material"
import { useDispatch, useSelector } from "react-redux";
import { getCartById } from "../../State/Cart/Action";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import { createAddress, getUserActiveAddress, updateBillingAddress } from "../../State/Address/Action";
import { privateAxios } from "../../../config/apiConfig";
import { useLocation, useNavigate } from "react-router-dom";
import { createOrder } from "../../State/Order/Action";
import defaultImage from "../../../Common/productDefaultImage.png";
import countryData from '../../../config/country-phone-code-and-length.json';

export default function CheckoutPage() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1)
    const [isGiftItem, setIsGiftItem] = useState(false)
    const [selectedAddress, setSelectedAddress] = useState(null)
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [selectedPayment, setSelectedPayment] = useState("")
    const [agreeToTerms, setAgreeToTerms] = useState(false)
    const dispatch = useDispatch();
    const jwt = localStorage.getItem("jwt");
    const { cart, address, order } = useSelector((store) => store);
    const [editingId, setEditingId] = useState(null);
    const [zipError, setZipError] = useState(""); // error msg store
    const { convert, selectedCountry, selectedCurrency, selectedCurrencyRate } = useConvertPrice();
    const [postOffice, setPostOffice] = useState([]);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const { id } = location.state || {};  // ✅ Product ID received

    const [formData, setFormData] = useState({
        name: "",
        addressLine1: "",
        addressLine2: "",
        mobile: "",
        country: "",
        city: "",
        state: "",
        zipcode: "",
        postOffice: "",
        nearestLocation: ""
    });

    const [country, setCountry] = useState(countryData.find(c => selectedCountry && c.label.includes(selectedCountry)) ||
        countryData.find(c => c.code === "IN"));
    const [phone, setPhone] = useState("");
    const [error, setError] = useState("");

    const handleCountryChange = (e) => {
        const selected = countryData.find(c => c.code === e.target.value);
        setCountry(selected);
        const fullNumber = `${selected.phone} ${phone}`;
        setFormData((prev) => ({ ...prev, mobile: fullNumber }));
        // ✅ Determine required phone length (local number length only)
        let requiredLength = null;

        if (selected && phone.trim()) {
            if (selected.phoneLength) {
                requiredLength = selected.phoneLength; // number or array
            } else if (selected.min && selected.max) {
                requiredLength = { min: selected.min, max: selected.max };
            }
        }

        // ✅ Validate local number
        let isValid = true;

        if (Array.isArray(requiredLength)) {
            isValid = requiredLength.includes(phone.length);
        } else if (typeof requiredLength === "number") {
            isValid = phone.length === requiredLength;
        } else if (requiredLength?.min && requiredLength?.max) {
            isValid = phone.length >= requiredLength.min && phone.length <= requiredLength.max;
        }

        if (!isValid) {
            setError(
                `Phone number must be ${Array.isArray(requiredLength)
                    ? requiredLength.join(" or ")
                    : requiredLength?.min
                        ? `${requiredLength.min}-${requiredLength.max}`
                        : requiredLength
                } digits`
            );
        } else {
            setError("");
            // ✅ Send full number to backend
            setFormData((prev) => ({ ...prev, mobile: fullNumber }));
        }
    };

    const handlePhoneChange = (e) => {
        // allow only digits for mobile & zipcode
        if (e.target.name === "mobile" && !/^\d*$/.test(e.target.value)) return;
        const cleaned = e.target.value.replace(/\D/g, ""); // ✅ Only digits
        // ✅ Construct full number (+countryCode + localNumber)
        const fullNumber = `${country.phone} ${cleaned}`;
        setPhone(e.target.value);
        // ✅ Determine required phone length (local number length only)
        let requiredLength = null;

        if (country && cleaned.trim()) {
            if (country.phoneLength) {
                requiredLength = country.phoneLength; // number or array
            } else if (country.min && country.max) {
                requiredLength = { min: country.min, max: country.max };
            }
        }

        // ✅ Validate local number
        let isValid = true;

        if (Array.isArray(requiredLength)) {
            isValid = requiredLength.includes(cleaned.length);
        } else if (typeof requiredLength === "number") {
            isValid = cleaned.length === requiredLength;
        } else if (requiredLength?.min && requiredLength?.max) {
            isValid = cleaned.length >= requiredLength.min && cleaned.length <= requiredLength.max;
        }

        if (!isValid) {
            setError(
                `Phone number must be ${Array.isArray(requiredLength)
                    ? requiredLength.join(" or ")
                    : requiredLength?.min
                        ? `${requiredLength.min}-${requiredLength.max}`
                        : requiredLength
                } digits`
            );
        } else {
            setError("");
            // ✅ Send full number to backend
            setFormData((prev) => ({ ...prev, mobile: fullNumber }));
        }

    };

    useEffect(() => {
        if (jwt && id) {
            dispatch(getCartById(id));
            setCountry(
                countryData.find(c => selectedCountry && c.label.includes(selectedCountry)) ||
                countryData.find(c => c.code === "IN")
            );
        }
        // if (jwt) {
        //     dispatch(getCart());
        // }
    }, [dispatch, jwt, id, selectedCountry]);

    useEffect(() => {
        if (jwt) dispatch(getUserActiveAddress());
    }, [dispatch, jwt, address?.createAddress, address?.updateBillingAddress, selectedCountry]);

    const handleCheckout = () => {
        if (selectedAddress && selectedCurrency && selectedCurrencyRate) {
            dispatch(createOrder(selectedAddress.id, selectedCurrency, selectedCurrencyRate, navigate, cart));
        }
    };

    const handleOpenAddressForm = () => {
        setShowAddressForm(true)
        if (selectedCountry) {
            setFormData((prev) => ({
                ...prev,
                country: selectedCountry,
            }));
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        // allow only digits for mobile & zipcode
        if (name === "zipcode" && !/^\d*$/.test(value)) return;

        // update state immediately
        setFormData((prev) => ({ ...prev, [name]: value }));

        // zipcode check after 6 digits
        if (name === "zipcode" && value.length === 6 && formData?.country?.toLowerCase() === "india") {
            try {
                setLoading(true)
                const res = await privateAxios.get(`user/address/check/${value}`);
                const data = res.data;

                if (!data.serviceable) {
                    setZipError("This zipcode is not serviceable.");
                    setPostOffice([]);
                    setFormData((prev) => ({
                        ...prev,
                        city: "",
                        state: "",
                    }));
                    setLoading(false)
                } else {
                    setZipError("");
                    setPostOffice(data.postOffice || []);

                    // ✅ use data.postOffice instead of postOffice state
                    const firstPO = data.postOffice?.[0];
                    if (firstPO) {
                        setFormData((prev) => ({
                            ...prev,
                            city: firstPO.District,
                            state: firstPO.State,
                            country: firstPO.Country || selectedCountry,
                        }));
                    }
                    setLoading(false)
                }
            } catch (error) {
                setZipError("This zipcode is not serviceable.");
                setPostOffice([]);
                setFormData((prev) => ({
                    ...prev,
                    city: "",
                    state: "",
                }));
                setLoading(false)
            }
        } else if (name === "zipcode") {
            setZipError(""); // clear error if not 6 digits yet
            setPostOffice([]);
        }

        if (name === "postOffice") {
            // ✅ use e.target.value instead of stale formData.postOffice
            const selectedPO = postOffice?.find((p) => p.Name === value);
            if (selectedPO) {
                setFormData((prev) => ({
                    ...prev,
                    city: selectedPO.District,
                    state: selectedPO.State,
                    country: selectedPO.Country || selectedCountry,
                }));
            }
        }
    };

    const handleAddOrUpdate = (e) => {
        e.preventDefault();
        if (!error && phone?.trim() !== "") {
            if (editingId) dispatch(updateBillingAddress(editingId, formData, handleCloseAddressForm, setSelectedAddress));
            else dispatch(createAddress(formData, handleCloseAddressForm, setSelectedAddress));
        }
    };

    const handleCloseAddressForm = () => {
        setFormData({ name: "", addressLine1: "", addressLine2: "", mobile: "", country: "", city: "", state: "", zipcode: "", postOffice: "", nearestLocation: "" });
        setEditingId(null);
        setShowAddressForm(false)
        setZipError("");
        setPostOffice([]);
        setError("");
        setPhone("");
        setCountry(
            countryData.find(c => selectedCountry && c.label.includes(selectedCountry)) ||
            countryData.find(c => c.code === "IN")
        );
    }

    const handleEditBillingAddress = async (address) => {
        const res = await privateAxios.get(`user/address/check/${address.billingZipcode}`);
        const data = res.data;
        if (data.serviceable) {
            setPostOffice(data.postOffice || []);
        }
        setFormData({
            name: address.name,
            addressLine1: address.billingAddressLine1,
            addressLine2: address.billingAddressLine2,
            mobile: address.billingMobile,
            country: address?.billingCountry,
            city: address.billingCity,
            state: address.billingState,
            zipcode: address.billingZipcode,
            postOffice: address.postOffice,
            nearestLocation: address.billingNearestLocation
        });
        setEditingId(address.id);
        setZipError("");
        setShowAddressForm(true)
        setError("");
        setPhone(
            address.billingMobile
                ? address.billingMobile.includes(' ')
                    ? address.billingMobile.slice(address.billingMobile.indexOf(' ') + 1)
                    : address.billingMobile // space नहीं है तो पूरा number use करें
                : '' // null या undefined के लिए empty string
        );
        setCountry(
            // countryData.find(c => address?.billingCountry && c.label.includes(address?.billingCountry))
            address.billingMobile
                ? countryData.find(
                    (c) => c.phone === address.billingMobile.split(' ')[0].replace('+', '')
                )
                : null
        );
    };

    const handleProceedToPayment = () => {
        setCurrentStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const fieldSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            fontSize: "0.875rem",
            fontFamily: "'Be Vietnam Pro', sans-serif",
            "&:hover fieldset": { borderColor: "#d8d2cc" },
            "&.Mui-focused fieldset": { borderColor: "#BB3D1F" },
        },
        "& .MuiInputLabel-root.Mui-focused": { color: "#BB3D1F" },
    };

    const renderAddressForm = () => (
        <Paper className="rounded-2xl border border-[#ececec] bg-white p-4 sm:p-5" elevation={0}>
            <Box className="mb-4 flex items-center justify-between">
                <Typography
                    sx={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: 20,
                        fontWeight: 500,
                        color: "#222",
                    }}
                >
                    {editingId ? "Edit Billing Address" : "Add New Address"}
                </Typography>
                <IconButton size="small" onClick={handleCloseAddressForm} className="h-6 w-6">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box
                className="relative"   // ✅ ये जोड़ा ताकि overlay सिर्फ form क्षेत्र में रहे
                component="form"
                onSubmit={handleAddOrUpdate}
                sx={{ py: 2 }}
            >

                <Grid container spacing={2}>
                    {loading && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                bgcolor: "rgba(255,255,255,0.6)", // transparent white overlay
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 2,
                                borderRadius: "16px",
                            }}
                        >
                            <CircularProgress size={50} thickness={4} />
                        </Box>
                    )}
                    {/* Form Fields */}
                    {[
                        { label: "Name", name: "name" },
                        { label: "Mobile", name: "mobile" },
                        { label: "Country", name: "country", readOnly: true },
                        { label: "Zipcode", name: "zipcode", maxLength: 6 },
                        { label: "City", name: "city", readOnly: formData?.country?.toLowerCase() === "india" ? true : false },
                        { label: "State", name: "state", readOnly: formData?.country?.toLowerCase() === "india" ? true : false },
                        { label: "Post Office", name: "postOffice", type: "select" },
                        { label: "Address Line 1", name: "addressLine1" },
                        { label: "Address Line 2", name: "addressLine2" },
                        // 👇 Country dropdown added here
                        { label: "Near Famous Location", name: "nearestLocation" },
                    ].map(({ label, name, maxLength, type, readOnly }) => (
                        <Grid item xs={12} sm={6} key={name}>
                            {type === "select" && formData?.country?.toLowerCase() === "india" ? (
                                <TextField
                                    select
                                    fullWidth
                                    label={label}
                                    name={name}
                                    size="small"
                                    value={formData[name] || ""}
                                    onChange={handleInputChange}
                                    required
                                    variant="outlined"
                                    sx={fieldSx}
                                >
                                    {postOffice?.map((p) => (
                                        <MenuItem key={p.Name} value={p.Name}>
                                            {p.Name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            ) : label === "Mobile" ? (
                                <TextField
                                    fullWidth
                                    label={label}
                                    name={name}
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    placeholder="Enter mobile number"
                                    variant="outlined"
                                    size="small"
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <TextField
                                                    select
                                                    value={country?.code}
                                                    onChange={handleCountryChange}
                                                    variant="standard"
                                                    size="small"
                                                    sx={fieldSx}
                                                    InputProps={{ disableUnderline: true }}
                                                >
                                                    {countryData.map((c) => (
                                                        <MenuItem key={c.code} value={c.code}>
                                                            {c.code} (+{c.phone})
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </InputAdornment>
                                        ),
                                    }}
                                    error={error}
                                    helperText={error !== "" ? error : ""}
                                    sx={fieldSx}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    label={label}
                                    name={name}
                                    disabled={readOnly}
                                    variant="outlined"
                                    size="small"
                                    value={formData[name]}
                                    onChange={handleInputChange}
                                    required={name !== "addressLine2"}
                                    error={name === "zipcode" ? Boolean(zipError) : ""}
                                    helperText={name === "zipcode" ? zipError : ""}
                                    inputProps={maxLength ? { maxLength } : undefined}
                                    sx={{
                                        ...fieldSx,
                                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                                            borderColor: name === "zipcode" && zipError ? "#dc2626" : "#BB3D1F",
                                        },
                                    }}
                                />
                            )}
                        </Grid>
                    ))}

                    {/* Buttons */}
                    <Grid item xs={12} mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#BB3D1F",
                                        color: "#fff",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        py: 1.2,
                                        fontSize: "0.8rem",
                                        letterSpacing: "0.06em",
                                        boxShadow: "none",
                                        "&:hover": { backgroundColor: "#9e3319", boxShadow: "none" },
                                    }}
                                    disabled={address?.actionLoading || loading}
                                >
                                    {address?.actionLoading ? editingId ? "EDITING..." : "ADDING..." : "ADDRESS"}
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleCloseAddressForm}
                                    sx={{
                                        py: 1.2,
                                        borderRadius: "8px",
                                        fontWeight: 500,
                                        fontSize: "0.8rem",
                                        color: "#333",
                                        borderColor: "#ececec",
                                        "&:hover": {
                                            backgroundColor: "#faf7f4",
                                            borderColor: "#d8d2cc",
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    )

    const renderShippingInfo = () => (
        <Box className="space-y-4">
            {showAddressForm ? renderAddressForm() : (
                <>
                    <Typography
                        sx={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: 20,
                            fontWeight: 500,
                            color: "#222",
                        }}
                    >
                        Select Your Shipping Address
                    </Typography>

                    <Grid container spacing={2}>
                        {address?.addresses?.map(
                            (address) =>
                                address.active && (
                                    <Grid item xs={12} md={4} sm={6} key={address.id}>
                                        <Paper
                                            className={`relative cursor-pointer rounded-2xl p-4 ${selectedAddress?.id === address.id
                                                ? "border border-[#BB3D1F] bg-[#faf7f4]"
                                                : "border border-[#ececec] bg-white hover:border-[#d8d2cc]"
                                                }`}
                                            onClick={() => setSelectedAddress(address)}
                                            elevation={0}
                                        >
                                            {selectedAddress?.id === address.id && (
                                                <CheckCircleIcon className="absolute right-2 top-2 text-[#BB3D1F]" fontSize="small" />
                                            )}
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Be Vietnam Pro', sans-serif",
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: "#333",
                                                    mb: 0.75,
                                                }}
                                            >
                                                {address.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Be Vietnam Pro', sans-serif",
                                                    fontSize: 13,
                                                    fontWeight: 300,
                                                    color: "#707070",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {address?.addressLine1},{address?.postOffice}, {address?.city}, {address?.state} ,{address?.country}, {address?.zipcode}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Be Vietnam Pro', sans-serif",
                                                    fontSize: 13,
                                                    fontWeight: 300,
                                                    color: "#707070",
                                                    mt: 0.5,
                                                }}
                                            >
                                                {address?.mobile}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                    </Grid>
                    {selectedAddress !== null && (
                        <>
                            <Typography
                                sx={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: 20,
                                    fontWeight: 500,
                                    color: "#222",
                                    mt: 1,
                                }}
                            >
                                Your Billing Address
                            </Typography>
                            <Grid item xs={12}>
                                <Paper
                                    className="relative mb-2 cursor-pointer rounded-2xl border border-[#BB3D1F] bg-[#faf7f4] p-4"
                                    onClick={() => handleEditBillingAddress(selectedAddress)}
                                    elevation={0}
                                >
                                    <CheckCircleIcon className="absolute right-2 top-2 text-[#BB3D1F]" fontSize="small" />
                                    <Typography
                                        sx={{
                                            fontFamily: "'Be Vietnam Pro', sans-serif",
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: "#333",
                                            mb: 0.75,
                                        }}
                                    >
                                        {selectedAddress?.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Be Vietnam Pro', sans-serif",
                                            fontSize: 13,
                                            fontWeight: 300,
                                            color: "#707070",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {selectedAddress?.billingAddressLine1},{selectedAddress?.billingPostOffice}, {selectedAddress?.billingCity}, {selectedAddress?.billingState},{selectedAddress?.billingCountry}, {selectedAddress?.billingZipcode}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Be Vietnam Pro', sans-serif",
                                            fontSize: 13,
                                            fontWeight: 300,
                                            color: "#707070",
                                            mt: 0.5,
                                        }}
                                    >
                                        {selectedAddress?.billingMobile}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </>
                    )}
                    <div className="pb-2">
                        <Box className="flex w-full flex-col gap-3 sm:flex-row sm:gap-4">
                            <Button
                                variant="contained"
                                className="flex-1 py-3 px-8 font-medium"
                                onClick={handleProceedToPayment}
                                sx={{
                                    backgroundColor: selectedAddress ? "#BB3D1F" : "#d4d4d4",
                                    borderRadius: "8px",
                                    boxShadow: "none",
                                    fontSize: 13,
                                    letterSpacing: "0.06em",
                                    "&:hover": { backgroundColor: selectedAddress ? "#9e3319" : "#d4d4d4", boxShadow: "none" },
                                    "&.Mui-disabled": { backgroundColor: "#e5e5e5", color: "#9a9a9a" },
                                }}
                                disabled={!selectedAddress}
                            >
                                PROCEED TO PAYMENT
                            </Button>

                            <Button
                                variant="outlined"
                                className="flex-1 py-3 px-8 font-medium"
                                sx={{
                                    borderRadius: "8px",
                                    borderColor: "#BB3D1F",
                                    color: "#BB3D1F",
                                    fontSize: 13,
                                    letterSpacing: "0.06em",
                                    "&:hover": { backgroundColor: "#faf7f4", borderColor: "#9e3319", color: "#9e3319" },
                                }}
                                onClick={handleOpenAddressForm}
                            >
                                ADD NEW ADDRESS
                            </Button>
                        </Box>
                    </div>
                </>
            )}
        </Box>
    )

    const paymentBtnSx = (active) =>
        active
            ? {
                backgroundColor: "#BB3D1F",
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#9e3319", boxShadow: "none" },
            }
            : {
                borderRadius: "8px",
                borderColor: "#ececec",
                color: "#333",
                "&:hover": { backgroundColor: "#faf7f4", borderColor: "#d8d2cc", color: "#333" },
            };

    const renderPaymentMethod = () => (
        <Box className="space-y-4">
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={4}>
                    <Button
                        variant={selectedPayment === "card" ? "contained" : "outlined"}
                        className="h-14 w-full text-sm font-medium"
                        onClick={() => setSelectedPayment("card")}
                        startIcon={<CreditCardIcon />}
                        sx={paymentBtnSx(selectedPayment === "card")}
                    >
                        CREDIT / DEBIT CARD
                    </Button>
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                    <Button
                        variant={selectedPayment === "netbanking" ? "contained" : "outlined"}
                        className="h-14 w-full text-sm font-medium"
                        onClick={() => setSelectedPayment("netbanking")}
                        startIcon={<AccountBalanceIcon />}
                        sx={paymentBtnSx(selectedPayment === "netbanking")}
                    >
                        NETBANKING
                    </Button>
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                    <Button
                        variant={selectedPayment === "upi" ? "contained" : "outlined"}
                        className="h-14 w-full text-sm font-medium"
                        onClick={() => setSelectedPayment("upi")}
                        startIcon={<CreditCardIcon />}
                        sx={paymentBtnSx(selectedPayment === "upi")}
                    >
                        UPI
                    </Button>
                </Grid>
            </Grid>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        sx={{ color: "#cfcfcf", "&.Mui-checked": { color: "#BB3D1F" } }}
                    />
                }
                label={
                    <Typography sx={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 13, color: "#333" }}>
                        I understand and agree to{" "}
                        <span className="cursor-pointer text-[#BB3D1F] hover:underline" onClick={() => navigate("/terms-condition")}>INARI FASHION T&C</span>
                    </Typography>
                }
            />

            <Button
                variant="contained"
                fullWidth
                className="py-3 font-medium"
                disabled={!selectedPayment || !agreeToTerms || order?.loading}
                sx={{
                    backgroundColor: "#BB3D1F",
                    borderRadius: "8px",
                    boxShadow: "none",
                    letterSpacing: "0.06em",
                    "&:hover": { backgroundColor: "#9e3319", boxShadow: "none" },
                    "&.Mui-disabled": { backgroundColor: "#e5e5e5", color: "#9a9a9a" },
                }}
                onClick={handleCheckout}
            >

                {order?.loading ? (
                    <CircularProgress
                        size={20}
                        sx={{
                            color: "white",
                        }}
                    />
                ) : (
                    <>
                        <CreditCard size={16} style={{ marginRight: 6 }} />
                        PAY NOW
                    </>
                )}
            </Button>
        </Box>
    )

    const getStepStyling = (step) => {
        if (step === currentStep) {
            return "border-l-4 border-l-[#BB3D1F] bg-[#faf7f4]"
        } else {
            return "bg-[#f7f7f7]"
        }
    }

    const getStepIcon = (step) => {
        switch (step) {
            case 1:
                return <LocationOnIcon fontSize="small" />
            case 2:
                return <PaymentIcon fontSize="small" />
            default:
                return null
        }
    }

    // NOTE: Replace these paths with your own static assets
    const PAYMENT_ICONS = [
        { src: "https://www.citypng.com/public/uploads/preview/download-visa-card-logo-icon-png-735811696866915avdywnhoab.png", alt: "Visa" },
        { src: "https://www.citypng.com/public/uploads/preview/png-mastercard-payment-logo-icon-701751695036654sl5blladjo.png", alt: "Mastercard" },
        { src: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQcm2oPQT2XfjwWV3MQUjsJViP1sMcEUxBK6Q&s", alt: "American Express" },
        { src: "https://www.citypng.com/public/uploads/preview/hd-paypal-logo-transparent-background-701751694777793hs5bxpddux.png", alt: "PayPal" },
    ];

    const summaryCard = "rounded-2xl border border-[#ececec] bg-white";

    return (
        <Box className="min-h-screen py-8 font-sans text-[#333]">
            <Box className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Grid container spacing={4}>
                    <Grid item xs={12} lg={8}>
                        <Box>
                            <Box>
                                <Box className={`${getStepStyling(1)} mb-4 rounded-xl p-4`}>
                                    <Box className="flex items-center justify-between">
                                        <Box className="flex items-center space-x-3">
                                            <Box className="flex items-center space-x-2 text-[#333]">
                                                {getStepIcon(1)}
                                                <Typography
                                                    sx={{
                                                        fontFamily: "'Be Vietnam Pro', sans-serif",
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        letterSpacing: "0.06em",
                                                        color: "#333",
                                                    }}
                                                >
                                                    1. SHIPPING & BILLING INFO
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {currentStep > 1 && selectedAddress && (
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Be Vietnam Pro', sans-serif",
                                                    fontSize: 13,
                                                    fontWeight: 300,
                                                    color: "#707070",
                                                }}
                                            >
                                                {selectedAddress?.billingPostOffice}, {selectedAddress?.billingCity}, {selectedAddress?.billingState}, {selectedAddress?.billingZipcode} {" "}
                                                <span className="cursor-pointer text-[#BB3D1F] hover:underline" onClick={() => { setCurrentStep(1) }}>Change</span>
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {currentStep === 1 && <Box className="bg-white pb-4 pt-1">{renderShippingInfo()}</Box>}
                            </Box>

                            <Box>
                                <Box className={`${getStepStyling(2)} mb-4 rounded-xl p-4`}>
                                    <Box className="flex items-center space-x-2 text-[#333]">
                                        {getStepIcon(2)}
                                        <Typography
                                            sx={{
                                                fontFamily: "'Be Vietnam Pro', sans-serif",
                                                fontSize: 13,
                                                fontWeight: 500,
                                                letterSpacing: "0.06em",
                                                color: "#333",
                                            }}
                                        >
                                            2. PAYMENT METHOD
                                        </Typography>
                                    </Box>
                                </Box>
                                {currentStep === 2 && <Box className="bg-white pb-4 pt-1">{renderPaymentMethod()}</Box>}
                            </Box>
                        </Box>

                        <Box className="mt-4 flex items-center justify-center space-x-4 p-2">
                            <Box className="flex items-center space-x-2">
                                <SecurityIcon className="text-[#707070]" />
                                <Typography
                                    sx={{
                                        fontFamily: "'Be Vietnam Pro', sans-serif",
                                        fontSize: 11,
                                        letterSpacing: "0.08em",
                                        color: "#707070",
                                    }}
                                >
                                    SECURE CHECKOUT
                                </Typography>
                            </Box>
                            <Box className="flex space-x-2">
                                {PAYMENT_ICONS.map((p) => (
                                    <img
                                        key={p.alt}
                                        src={p.src}
                                        alt={p.alt}
                                        width={40} height={24}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                        <aside className="w-full">
                            <Typography
                                sx={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: { xs: 22, md: 24 },
                                    fontWeight: 500,
                                    color: "#222",
                                    mb: 2,
                                }}
                            >
                                Cart Summary
                            </Typography>
                            <Box className={`${summaryCard} p-5`}>
                                <div className="flex items-center justify-between py-1">
                                    <span className="font-sans text-[14px] font-light text-[#333]">Cart Total</span>
                                    <span className="font-sans text-[14px]">{convert(cart?.cart?.totalPrice) + " " + selectedCurrency}</span>
                                </div>
                                <div className="flex items-center justify-between py-1 text-[#2f9e44]">
                                    <span className="font-sans text-[14px] font-light">Total Discount</span>
                                    <span className="font-sans text-[14px]">{"-" + convert(cart?.cart?.totalDiscount || 0) + " " + selectedCurrency}</span>
                                </div>
                                <div className="flex items-center justify-between py-1 text-[#2f9e44]">
                                    <span className="font-sans text-[14px] font-light">Shipping</span>
                                    <span className="font-sans text-[14px]">Free</span>
                                </div>
                                {cart?.cart?.customizationCharge > 0 && (
                                    <div className="flex items-center justify-between text-[#BB3D1F]">
                                        <span className="font-sans text-[14px] font-light">Customization Charge</span>
                                        <span className="font-sans text-[14px]">{"+" + convert(cart?.cart?.customizationCharge || 0) + " " + selectedCurrency}</span>
                                    </div>
                                )}

                                <Box className="mt-3 flex items-end justify-between border-t border-[#ececec] pt-3">
                                    <Typography
                                        sx={{
                                            fontFamily: "'Be Vietnam Pro', sans-serif",
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#222",
                                        }}
                                    >
                                        Total Payable
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: "'Be Vietnam Pro', sans-serif",
                                            fontSize: 18,
                                            fontWeight: 600,
                                            color: "#222",
                                        }}
                                    >
                                        {convert(cart?.cart?.cartTotalPrice)} {selectedCurrency}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box className={`${summaryCard} mt-4 p-5`}>
                                <Box className="flex items-center justify-between">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isGiftItem}
                                                onChange={(e) => setIsGiftItem(e.target.checked)}
                                                size="small"
                                                sx={{
                                                    color: "#cfcfcf",
                                                    "&.Mui-checked": { color: "#BB3D1F" },
                                                }}
                                            />
                                        }
                                        label={<span className="font-sans text-[13px] font-light text-[#333]">This is a gift item</span>}
                                        className="m-0"
                                    />
                                    <Button variant="text" className="!text-[#BB3D1F] !text-[12px] !font-normal !normal-case hover:!bg-transparent">
                                        (Know More)
                                    </Button>
                                </Box>
                            </Box>

                            <Typography
                                sx={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: { xs: 22, md: 24 },
                                    fontWeight: 500,
                                    color: "#222",
                                    mt: 3,
                                    mb: 2,
                                }}
                            >
                                Product Summary
                            </Typography>
                            <Box className={`${summaryCard} overflow-hidden divide-y divide-[#ececec]`}>
                                {cart?.cartItems?.map((item, index) => (
                                    <ProductMini key={item?.id || index} item={item} />
                                ))}
                            </Box>
                        </aside>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}

function ProductMini({ item }) {
    const { convert, customerPriceWithCharge, payableAmount, selectedCurrency } = useConvertPrice();
    const navigate = useNavigate();
    const [openCustomDialog, setOpenCustomDialog] = useState(false);
    const [selectedCustomSize, setSelectedCustomSize] = useState(null);

    const handleOpenCustomDialog = (item) => {
        // const savedSizes = JSON.parse(localStorage.getItem("customSizes")) || [];
        //  // Find by productId
        // const sizeData = savedSizes.find(
        //   (item) => Number(item?.productId) === Number(item?.product?.id)
        // ) || null;

        const savedSizes = JSON.parse(localStorage.getItem("customSizes")) || {};

        // direct key access
        const sizeData = savedSizes[item?.product?.id] || null;

        setSelectedCustomSize(sizeData);
        setOpenCustomDialog(true);
    };

    const handleCloseCustomDialog = () => {
        setOpenCustomDialog(false);
        setSelectedCustomSize(null);
    };
    const productImage =
        item?.product?.images?.find((img) => img?.isPrimary && img?.productColor?.color?.name === item?.variant?.productColor?.color?.name)?.fullUrl ||
        defaultImage;

    const hasDiscount = item?.variant?.viewTotalDiscount > 0;
    const sizeLabel =
        item?.size?.toLowerCase() === "custom tailor"
            ? item?.size
            : item?.variant?.size?.name || item?.size;

    return (
        <div className="grid grid-cols-[72px_1fr] gap-3 p-3 sm:grid-cols-[80px_1fr] sm:p-4">
            <div
                className="aspect-[3/4] cursor-pointer overflow-hidden rounded-xl bg-[#f6f2ed]"
                onClick={() => navigate(`/product/${item?.product?.id}`)}
            >
                <img
                    src={productImage}
                    alt="Product"
                    className="h-full w-full object-cover"
                />
            </div>

            <div className="flex min-w-0 flex-col justify-between">
                <div>
                    <Typography
                        sx={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontStyle: "italic",
                            fontSize: 14,
                            fontWeight: 400,
                            color: "#333",
                            lineHeight: 1.3,
                        }}
                    >
                        {item?.product?.brand?.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Be Vietnam Pro', sans-serif",
                            fontSize: 12,
                            fontWeight: 300,
                            color: "#707070",
                            mt: 0.25,
                        }}
                    >
                        {item?.product?.title}
                    </Typography>
                </div>

                <Box className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
                    <Typography sx={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 13, fontWeight: 600, color: "#333" }}>
                        {payableAmount(
                            customerPriceWithCharge(
                                selectedCurrency,
                                item?.variant?.viewCustomerAmount
                            ),
                            item?.variant?.viewTotalDiscount
                        )}{" "}
                        {selectedCurrency}
                    </Typography>
                    {hasDiscount && (
                        <>
                            <Typography sx={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 12, color: "#9a9a9a", textDecoration: "line-through" }}>
                                {convert(
                                    customerPriceWithCharge(
                                        selectedCurrency,
                                        item?.variant?.viewCustomerAmount
                                    )
                                )}{" "}
                                {selectedCurrency}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 12, fontWeight: 500, color: "#2f9e44" }}>
                                {item.variant.viewTotalDiscount}% OFF
                            </Typography>
                        </>
                    )}
                </Box>

                <Box className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-sans text-[12px] font-light text-[#707070]">
                    {item?.variant?.productColor?.color?.name && (
                        <span>Color: {item.variant.productColor.color.name}</span>
                    )}
                    {sizeLabel && (
                        <button
                            type="button"
                            className={`border-0 bg-transparent p-0 text-[12px] ${
                                item?.size?.toLowerCase() === "custom tailor"
                                    ? "cursor-pointer hover:underline"
                                    : "cursor-default"
                            }`}
                            onClick={() => {
                                if (item?.size?.toLowerCase() === "custom tailor") {
                                    handleOpenCustomDialog(item);
                                }
                            }}
                        >
                            Size: {sizeLabel}
                        </button>
                    )}
                    {item?.size?.toLowerCase() === "custom tailor" && (
                        <Typography sx={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 11, color: "#BB3D1F", width: "100%" }}>
                            Customization Charge: {convert(item?.product?.customizationPrice)} {selectedCurrency}
                        </Typography>
                    )}
                </Box>
            </div>
            {/* Size View Details Dialog */}
            <Dialog open={openCustomDialog} onClose={handleCloseCustomDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Custom Size Details
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseCustomDialog}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {selectedCustomSize ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} >
                                <TextField
                                    label="Product Title"
                                    value={item?.product?.title}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            {Object.entries(selectedCustomSize)
                                // unwanted fields hata do
                                .filter(([key, value]) =>
                                    value !== null &&
                                    value !== undefined &&
                                    value !== "" &&
                                    !["id", "productId"].includes(key)
                                )
                                .map(([key, value]) => {
                                    // Label banane ka helper
                                    const label = key
                                        .replace(/([A-Z])/g, " $1")   // camelCase -> camel Case
                                        .replace(/_/g, " ")           // snake_case -> snake case
                                        .replace(/^./, (str) => str.toUpperCase()); // pehla letter capitalize

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={key}>
                                            <TextField
                                                label={label}
                                                value={String(value)}
                                                size="small"
                                                fullWidth
                                                InputProps={{ readOnly: true }}
                                            />
                                        </Grid>
                                    );
                                })}
                        </Grid>
                    ) : (
                        <Typography color="text.secondary">
                            No custom size details found.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}